import type {
  AgentCapabilityMention,
  AgentConversationStartResult,
  AgentConversationStopResult,
  AgentExtensionView,
  AgentMemoryClusterView,
  AgentMemoryReconciliationView,
  AgentMemoryView,
  AgentModelProfileView,
  AgentEffectiveModelView,
  BuilderModelPreference,
  BuilderModelPreferenceView,
  AgentRunView,
  AgentSessionHistory,
  AgentSessionPageView,
  AgentSessionPromptDebugView,
  AgentSessionView,
  AgentToolCatalogView,
  AgentView,
  ModelProviderKind,
  ModelConnectionTestInput,
  ModelConnectionTestResult,
  ExecutionTargetPreference,
  ThinkingEffort,
  ComponentPreviewElementReference,
} from 'shared';
import { http } from './client';
import { validateAgentAttachments } from '@/utils/agent-attachment';

export const agentApi = {
  listMentionCapabilities: (workspaceId: string, agentId: string) =>
    http.get<AgentCapabilityMention[]>(`/workspaces/${workspaceId}/agents/${agentId}/mention-capabilities`),
  list: (workspaceId: string) => http.get<AgentView[]>(`/workspaces/${workspaceId}/agents`),
  create: (
    workspaceId: string,
    input: {
      kind: AgentView['kind'];
      componentId?: string;
      name: string;
      description?: string;
      systemPrompt?: string;
    },
  ) => http.post<AgentView>(`/workspaces/${workspaceId}/agents`, input),
  updateConfig: (
    workspaceId: string,
    agentId: string,
    input: Partial<
      Pick<AgentView, 'name' | 'modelProfileId' | 'modelSource' | 'systemPrompt' | 'modelOverrides'>
    >,
  ) => http.patch<AgentView>(`/workspaces/${workspaceId}/agents/${agentId}/settings`, input),
  myModelPreference: (workspaceId: string, agentId: string) =>
    http.get<{ modelProfileId: string | null; modelSource: 'user' | 'system' | null }>(
      `/workspaces/${workspaceId}/agents/${agentId}/my-model-preference`,
    ),
  setMyModelPreference: (
    workspaceId: string,
    agentId: string,
    modelProfileId: string | null,
    modelSource: 'user' | 'system' | null = null,
  ) =>
    http.patch<{ modelProfileId: string | null; modelSource: 'user' | 'system' | null }>(
      `/workspaces/${workspaceId}/agents/${agentId}/my-model-preference`,
      { modelProfileId, modelSource },
    ),
  applyComponentDefaults: (workspaceId: string, agentId: string) =>
    http.post<AgentView>(`/workspaces/${workspaceId}/agents/${agentId}/component-defaults/apply`),
  regenerateAvatar: (workspaceId: string, agentId: string) =>
    http.post<AgentView>(`/workspaces/${workspaceId}/agents/${agentId}/avatar/regenerate`),
  useDefaultAvatar: (workspaceId: string, agentId: string) =>
    http.post<AgentView>(`/workspaces/${workspaceId}/agents/${agentId}/avatar/default`),
  listModelProfiles: () => http.get<AgentModelProfileView[]>('/account/model-profiles'),
  listSystemModelOptions: () =>
    http.get<
      Array<{
        _id: string;
        name: string;
        model: string;
        providerKind: ModelProviderKind;
        supportsThinkingEffort: boolean;
        supportsMultimodal: boolean;
        contextWindowTokens?: number;
        inputCreditsPerMillion: number;
        outputCreditsPerMillion: number;
      }>
    >('/account/model-profiles/system-options'),
  getDefaultModelPreference: () =>
    http.get<{
      modelProfileId: string | null;
      modelSource: 'system' | null;
      effectiveModel: AgentEffectiveModelView;
    }>('/account/model-profiles/default-preference'),
  getBuilderModelPreference: () =>
    http.get<BuilderModelPreferenceView>('/account/model-profiles/builder-preference'),
  setBuilderModelPreference: (preference: BuilderModelPreference) =>
    http.patch<BuilderModelPreferenceView>(
      '/account/model-profiles/builder-preference',
      preference,
    ),
  setDefaultSystemModel: (modelProfileId: string) =>
    http.patch<{ modelProfileId: string; modelSource: 'system' }>(
      '/account/model-profiles/default-system-model',
      { modelProfileId },
    ),
  createModelProfile: (input: {
    desktopDeviceId?: string | null;
    name: string;
    baseUrl: string;
    model: string;
    apiKey: string;
    providerKind?: ModelProviderKind;
    isDefault?: boolean;
    thinkingEnabled?: boolean;
    supportsThinkingEffort?: boolean;
    supportsMultimodal?: boolean;
    contextWindowTokens?: number;
  }) => http.post<AgentModelProfileView>('/account/model-profiles', input),
  updateModelProfile: (
    profileId: string,
    input: {
      name: string;
      baseUrl: string;
      model: string;
      apiKey?: string;
      desktopDeviceId?: string | null;
      providerKind?: ModelProviderKind;
      isDefault?: boolean;
      thinkingEnabled?: boolean;
      supportsThinkingEffort?: boolean;
      supportsMultimodal?: boolean;
      contextWindowTokens?: number;
    },
  ) => http.patch<AgentModelProfileView>(`/account/model-profiles/${profileId}`, input),
  discoverModels: (input: {
    baseUrl: string;
    apiKey: string;
    providerKind?: ModelProviderKind;
    desktopDeviceId?: string | null;
  }) => http.post<string[]>('/account/model-profiles/discover-models', input),
  testModelConnection: (input: ModelConnectionTestInput) =>
    http.post<ModelConnectionTestResult>('/account/model-profiles/test-connection', input),
  listExtensions: (workspaceId: string) =>
    http.get<AgentExtensionView[]>(`/workspaces/${workspaceId}/agents/extensions`),
  listMemories: (workspaceId: string, agentId: string) =>
    http.get<AgentMemoryView[]>(`/workspaces/${workspaceId}/agents/${agentId}/memories`),
  createMemory: (
    workspaceId: string,
    agentId: string,
    input: { title: string; content: string; visibility?: 'private' | 'shared'; tags?: string[] },
  ) => http.post<AgentMemoryView>(`/workspaces/${workspaceId}/agents/${agentId}/memories`, input),
  listToolCatalog: (workspaceId: string) =>
    http.get<AgentToolCatalogView[]>(`/workspaces/${workspaceId}/agents/tool-catalog`),
  listSessions: (
    workspaceId: string,
    agentId: string,
    input: { cursor?: string; limit?: number; origin?: import('shared').AgentSessionOrigin; capabilityDraftId?: string } = {},
  ) =>
    http.get<AgentSessionPageView>(`/workspaces/${workspaceId}/agents/${agentId}/sessions`, {
      params: input,
    }),
  createSession: (
    workspaceId: string,
    agentId: string,
    options: { thinkingEnabled?: boolean; thinkingEffort?: ThinkingEffort; modelPreference?: BuilderModelPreference | null; capabilityDraftId?: string } = {},
  ) => http.post<AgentSessionView>(`/workspaces/${workspaceId}/agents/${agentId}/sessions`, options),
  listDreamSessions: (workspaceId: string, agentId: string) =>
    http.get<AgentSessionView[]>(`/workspaces/${workspaceId}/agents/${agentId}/dream-sessions`),
  effectiveModel: (workspaceId: string, agentId: string, options: { sessionId?: string | null; modelPreference?: BuilderModelPreference | null } = {}) =>
    http.get<AgentEffectiveModelView>(
      `/workspaces/${workspaceId}/agents/${agentId}/effective-model`,
      { params: { sessionId: options.sessionId ?? undefined, modelPreference: options.modelPreference ? JSON.stringify(options.modelPreference) : undefined } },
    ),
  startConversation: async (
    workspaceId: string,
    agentId: string,
    content: string,
    attachments: File[] = [],
    options: {
      capabilityDraftId?: string;
      modelPreference?: BuilderModelPreference | null;
      thinkingEnabled?: boolean;
      thinkingEffort?: ThinkingEffort;
      executionTarget?: ExecutionTargetPreference;
      desktopTaskTarget?: ExecutionTargetPreference;
    } = {},
  ) =>
    http.post<AgentConversationStartResult>(
      `/workspaces/${workspaceId}/agents/${agentId}/messages`,
      await messagePayload(content, attachments, options),
    ),
  getSessionHistory: (workspaceId: string, sessionId: string) =>
    http.get<AgentSessionHistory>(
      `/workspaces/${workspaceId}/agents/sessions/${sessionId}/history`,
    ),
  getSessionPromptDebug: (workspaceId: string, sessionId: string) =>
    http.get<AgentSessionPromptDebugView | null>(
      `/workspaces/${workspaceId}/agents/sessions/${sessionId}/prompt-debug`,
    ),
  associateCapabilitySession: (workspaceId: string, sessionId: string, capabilityDraftId: string) =>
    http.patch<AgentSessionView>(`/workspaces/${workspaceId}/agents/sessions/${sessionId}/capability-draft`, { capabilityDraftId }),
  getAttachmentContent: (
    workspaceId: string,
    sessionId: string,
    messageId: string,
    attachmentId: string,
  ) =>
    http.getBlob(
      `/workspaces/${workspaceId}/agents/sessions/${sessionId}/messages/${messageId}/attachments/${attachmentId}/content`,
    ),
  deleteSession: (workspaceId: string, sessionId: string) =>
    http.delete<{ ok: true }>(`/workspaces/${workspaceId}/agents/sessions/${sessionId}`),
  branchSession: (workspaceId: string, sessionId: string, messageId: string) =>
    http.post<AgentSessionView>(
      `/workspaces/${workspaceId}/agents/sessions/${sessionId}/messages/${messageId}/branch`,
    ),
  withdrawPendingInterjection: (workspaceId: string, sessionId: string, messageId: string) =>
    http.delete<{ removed: boolean }>(
      `/workspaces/${workspaceId}/agents/sessions/${sessionId}/pending-interjections/${messageId}`,
    ),
  updateSessionModel: (workspaceId: string, sessionId: string, modelPreference: BuilderModelPreference | null) =>
    http.patch<{ modelPreference: BuilderModelPreference | null }>(`/workspaces/${workspaceId}/agents/sessions/${sessionId}/model`, { modelPreference }),
  updateSessionThinking: (
    workspaceId: string,
    sessionId: string,
    input: { thinkingEnabled?: boolean; thinkingEffort?: ThinkingEffort },
  ) =>
    http.patch<{ thinkingEnabled: boolean | null; thinkingEffort: ThinkingEffort | null }>(
      `/workspaces/${workspaceId}/agents/sessions/${sessionId}/thinking`,
      input,
    ),
  updateSessionExecutionTarget: (
    workspaceId: string,
    sessionId: string,
    executionTarget: ExecutionTargetPreference,
  ) =>
    http.patch<{ executionTarget: ExecutionTargetPreference }>(
      `/workspaces/${workspaceId}/agents/sessions/${sessionId}/execution-target`,
      { executionTarget },
    ),
  sendMessage: async (
    workspaceId: string,
    sessionId: string,
    content: string,
    attachments: File[] = [],
    options: {
      previewElement?: ComponentPreviewElementReference;
      resetFromMessageId?: string;
      thinkingEnabled?: boolean;
      thinkingEffort?: ThinkingEffort;
      desktopTaskTarget?: ExecutionTargetPreference;
    } = {},
  ) =>
    http.post<{
      runId: string | null;
      queued: boolean;
      messageId: string;
      previewElementAccepted?: boolean;
    }>(
      `/workspaces/${workspaceId}/agents/sessions/${sessionId}/messages`,
      await messagePayload(content, attachments, options),
    ),
  getRun: (workspaceId: string, runId: string) =>
    http.get<AgentRunView>(`/workspaces/${workspaceId}/agents/runs/${runId}`),
  cancelRun: (workspaceId: string, runId: string, reason?: string) =>
    http.post<AgentRunView>(`/workspaces/${workspaceId}/agents/runs/${runId}/cancel`, { reason }),
  stopConversation: (workspaceId: string, sessionId: string, runId: string) =>
    http.post<AgentConversationStopResult>(
      `/workspaces/${workspaceId}/agents/sessions/${sessionId}/stop`,
      { runId, reason: '用户停止回复' },
    ),
  retryRun: (workspaceId: string, runId: string) =>
    http.post<AgentRunView>(`/workspaces/${workspaceId}/agents/runs/${runId}/retry`),
  listMemoryReconciliations: (workspaceId: string, agentId: string) =>
    http.get<AgentMemoryReconciliationView[]>(
      `/workspaces/${workspaceId}/agents/${agentId}/memory-reconciliations`,
    ),
  listMemoryClusters: (workspaceId: string, agentId: string) =>
    http.get<AgentMemoryClusterView[]>(
      `/workspaces/${workspaceId}/agents/${agentId}/memory-clusters`,
    ),
  getMemoryRelations: (workspaceId: string, agentId: string, memoryId: string) =>
    http.get<{ memory: AgentMemoryView; related: AgentMemoryView[] }>(
      `/workspaces/${workspaceId}/agents/${agentId}/memories/${memoryId}/relations`,
    ),
  applyMemoryReconciliation: (workspaceId: string, id: string) =>
    http.post<AgentMemoryView>(
      `/workspaces/${workspaceId}/agents/memory-reconciliations/${id}/apply`,
    ),
  rollbackMemoryReconciliation: (workspaceId: string, id: string) =>
    http.post<{ ok: true }>(
      `/workspaces/${workspaceId}/agents/memory-reconciliations/${id}/rollback`,
    ),
};

async function messagePayload(
  content: string,
  attachments: File[],
  options: {
    capabilityDraftId?: string;
    modelPreference?: BuilderModelPreference | null;
    previewElement?: ComponentPreviewElementReference;
    resetFromMessageId?: string;
    thinkingEnabled?: boolean;
    thinkingEffort?: ThinkingEffort;
    executionTarget?: ExecutionTargetPreference;
    desktopTaskTarget?: ExecutionTargetPreference;
  } = {},
): Promise<
  | {
      content: string;
      previewElement?: ComponentPreviewElementReference;
      resetFromMessageId?: string;
      thinkingEnabled?: boolean;
      thinkingEffort?: ThinkingEffort;
      executionTarget?: ExecutionTargetPreference;
      desktopTaskTarget?: ExecutionTargetPreference;
    }
  | FormData
> {
  if (!attachments.length) return { content, ...options };
  await validateAgentAttachments(attachments);
  const form = new FormData();
  form.append('content', content);
  if (options.previewElement) form.append('previewElement', JSON.stringify(options.previewElement));
  if (options.resetFromMessageId) form.append('resetFromMessageId', options.resetFromMessageId);
  if (options.capabilityDraftId) form.append('capabilityDraftId', options.capabilityDraftId);
  if (options.thinkingEnabled !== undefined)
    form.append('thinkingEnabled', String(options.thinkingEnabled));
  if (options.thinkingEffort !== undefined) form.append('thinkingEffort', options.thinkingEffort);
  if (options.modelPreference) form.append('modelPreference', JSON.stringify(options.modelPreference));
  if (options.executionTarget)
    form.append('executionTarget', JSON.stringify(options.executionTarget));
  if (options.desktopTaskTarget)
    form.append('desktopTaskTarget', JSON.stringify(options.desktopTaskTarget));
  attachments.forEach((file) => form.append('attachments', file));
  return form;
}
